import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import { get, isArray, template } from 'lodash';
import { filter, takeUntil } from 'rxjs/operators';

import type { RequestError } from '@swf/core/api/request';
import type {
  SelectorCreator,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type {
  FetchServiceMeta,
  ReducerPayload,
  UpdatePayload,
  WidgetData,
  WidgetReducer
} from '@swf/core/types/Widget';
import { canUseDOM } from '@swf/core/utils';
import { fetchProperties } from '@swf/core/utils/widgets';

/* eslint-disable @typescript-eslint/no-explicit-any */

type WidgetState = null | {
  [id: string]: WidgetData;
};

interface FetchPayload {
  key: string;
  params?: Record<string, string>;
  meta?: FetchServiceMeta;
  refresh?: boolean;
}

interface ResponsePayload extends ReducerPayload {
  reducer?: WidgetReducer;
}

export interface ClearPayload {
  key?: string | number;
}

const reducers = {
  response: (state: WidgetState, payload: ResponsePayload): WidgetState => {
    if (payload.reducer) return payload.reducer(state || undefined, payload);
    return { ...state, [payload.key]: payload.data };
  },
  update: (state: WidgetState, { id, reducer }: UpdatePayload): WidgetState => ({
    ...state,
    [id]: reducer(state?.[id])
  }),
  clear: (state: WidgetState, { key }: ClearPayload = {}): WidgetState =>
    produce(state, (draft = {}) => {
      if (isArray(draft) && typeof key === 'number') {
        draft.splice(key, 1);
      } else if (draft && key) {
        delete draft[key];
      } else {
        return null;
      }
    }),
  set: (state: WidgetState, payload: UpdatePayload): WidgetState => payload.reducer(state)
};

const effects = {
  async fetch(
    { key, params, meta = {}, refresh }: FetchPayload,
    {
      rootState,
      request,
      subscribe,
      requestStream,
      dispatch,
      actions,
      widget,
      select
    }: WidgetEffectParams<any>
  ) {
    const { type, id, services = {} } = widget;
    const { serviceReducers, serviceErrorReducers, errorHandler } = await fetchProperties(type);
    const { params: defaults, config } = services[key];
    const { templateValues = config.templateValues, reducer: metaReducer, ...rest } = meta;

    const name = templateValues ? template(config.name)(templateValues) : config.name;
    const currentData = select[id].get(rootState) || {};
    const options = { service: name, body: params || defaults, method: config.method };
    const reducer = get(serviceReducers, key, metaReducer);
    const payload = { id, key, config, meta: rest };
    const nextRefresh = actions.pipe(
      filter(({ type, payload }) => type === `${id}/fetch` && payload.refresh)
    );
    const logout =
      config.onAuth && !config.onLoad && actions.pipe(filter(({ type }) => type === 'auth/logout'));

    const { response } = dispatch[id];

    if (config.requestStream && canUseDOM) {
      let initialData = currentData[key];

      if (initialData && meta.patch) {
        const { body: data } = await request(options);
        initialData = produce(initialData, (draft: typeof initialData) => {
          applyPatch(draft, data);
        });
        response({ ...payload, data: initialData });
      } else if (refresh) {
        initialData = undefined;
      }
      let sub = requestStream(options, initialData).pipe(takeUntil(nextRefresh));
      if (logout) sub = sub.pipe(takeUntil(logout));
      sub.subscribe((data) => response({ ...payload, data }));
    } else if (config.subscribe) {
      let sub = subscribe(name);
      if (logout) sub = sub.pipe(takeUntil(logout));
      sub.subscribe(({ body: data }) => response({ ...payload, data, reducer }));
    } else {
      try {
        const { body: data } = await request(options);
        response({ ...payload, data, reducer, meta });
      } catch (e) {
        const error = e as RequestError;
        const errorReducer = get(serviceErrorReducers, key);
        if (errorHandler) {
          const data = await errorHandler(currentData, { ...payload, params, error });
          response({ ...payload, data });
        } else if (errorReducer) {
          const { headers, body: data } = error;
          response({
            ...payload,
            data,
            reducer: errorReducer,
            meta: { statusCode: headers?.statusCode }
          });
        }
      }
    }
  },
  async preload(payload: undefined, params: WidgetEffectParams<any>) {
    const { rootState, dispatch, widget, select, actions } = params;
    if (!widget.services) return;
    const isAuthenticated = select.session.isClientAuthenticated(rootState);
    const { id, services } = widget;
    // avoid dispatch of additional actions during preload
    const { fetch } = defaultModel.effects;

    await Promise.all(
      Object.keys(services)
        .filter((key) => {
          const { onLoad, onAuth, subscribe } = services[key].config;
          return (canUseDOM || !subscribe) && (onLoad || (onAuth && isAuthenticated));
        })
        .map((key) => fetch({ key }, params))
    );

    if (canUseDOM) {
      actions.pipe(takeUntil(widget.unmount)).subscribe(({ type, state }) => {
        if (type === 'session/response' && select.session.isClientAuthenticated(state)) {
          Object.keys(services)
            .filter((key) => services[key].config.onAuth)
            .forEach((key) => fetch({ key }, params));
        }
        if (type === 'auth/logout') {
          Object.keys(services)
            .filter((key) => services[key].config.onAuth && !services[key].config.onLoad)
            .forEach((key) => dispatch[id].clear({ key }));
        }
      });
    }
  }
};

const selectors = (slice: Slicer<any>, createSelector: SelectorCreator) => ({
  get() {
    return slice((state) => state || undefined);
  }
});

export interface DefaultModel extends WidgetModelConfig<any, any> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const defaultModel: DefaultModel = {
  state: null,
  reducers,
  effects,
  selectors
};
